import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { IncomingHttpHeaders } from 'http'
import type { DayAvailability } from '@/types'

import Head from 'next/head'
import { getAvailability } from '@/lib/booking'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { BookingProvider } from '@/components/landing/booking-provider'
import HeroSection from '@/components/landing/hero-section'
import HoursSection from '@/components/landing/hours-section'
import ServicesSection from '@/components/landing/services-section'
import ScheduleSection from '@/components/landing/schedule-section'
import AvailabilitySection from '@/components/landing/availability-section'
import LocationSection from '@/components/landing/location-section'
import { siteConfig } from '@/constants/site'
import { clinic } from '@/constants/clinic'

interface HomeProps {
    availability: DayAvailability[]
    origin: string
}

const firstHeader = (headers: IncomingHttpHeaders, name: string): string | undefined => {
    const value = headers[name]
    const raw = Array.isArray(value) ? value[0] : value

    return raw?.split(',')[0]?.trim() || undefined
}

const resolveOrigin = (headers: IncomingHttpHeaders): string => {
    const host = firstHeader(headers, 'x-forwarded-host') ?? firstHeader(headers, 'host')
    const protocol = firstHeader(headers, 'x-forwarded-proto') === 'http' ? 'http' : 'https'

    if (!host || !/^[a-z0-9.-]+(:\d+)?$/i.test(host)) return ''

    return `${protocol}://${host}`
}

const buildStructuredData = (origin: string): string => {
    const data = {
        '@context': 'https://schema.org',
        '@type': 'MedicalClinic',
        name: clinic.name,
        description: siteConfig.description,
        url: origin ? `${origin}/` : undefined,
        image: origin ? `${origin}/images/poli-umum.jpg` : undefined,
        logo: origin ? `${origin}/images/logo-mark.png` : undefined,
        telephone: '+62-22-6674146',
        email: clinic.email.label,
        address: {
            '@type': 'PostalAddress',
            streetAddress: 'Jl. Kerkof No. 32, Kelurahan Leuwigajah',
            addressLocality: 'Cimahi Selatan, Kota Cimahi',
            addressRegion: 'Jawa Barat',
            postalCode: '40532',
            addressCountry: 'ID'
        },
        medicalSpecialty: ['PrimaryCare', 'Dentistry', 'Obstetric', 'Emergency'],
        openingHoursSpecification: [
            {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                opens: '07:30',
                closes: '11:30'
            },
            {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                opens: '15:30',
                closes: '19:30'
            }
        ],
        sameAs: [clinic.instagram.href]
    }

    return JSON.stringify(data).replace(/</g, '\\u003c')
}

const Home: FC<HomeProps> = ({ availability, origin }): ReactNode => {
    return (
        <BookingProvider days={availability}>
            <Layout>
                <Seo
                    url={origin ? `${origin}/` : undefined}
                    ogImage={origin ? `${origin}/images/poli-umum.jpg` : undefined}
                />

                <Head>
                    <script
                        type='application/ld+json'
                        dangerouslySetInnerHTML={{ __html: buildStructuredData(origin) }}
                    />
                </Head>

                <HeroSection />
                <HoursSection />
                <ServicesSection />
                <ScheduleSection />
                <AvailabilitySection />
                <LocationSection />
            </Layout>
        </BookingProvider>
    )
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async ({ req }) => {
    const availability = await getAvailability()

    return {
        props: {
            availability,
            origin: resolveOrigin(req.headers)
        }
    }
}

export default Home
